import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
// Assuming you have run `npx shadcn-ui@latest init` and added these components
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { analytics, trackLandingPageView } from '../services/analytics';
import { trackPageView, trackButtonClick } from '../services/ga4Analytics';
import SEO from '../components/SEO';
import Footer from '../components/Footer';
import { industryQuotes } from '../data/industryQuotes';
import { supabase } from '../lib/supabase';

// --- Icon Components ---
// Using inline SVGs for icons makes the component self-contained and avoids dependency issues.
const CheckIcon = (props) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const ZapIcon = (props) => (
    <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
    </svg>
);

const BarChartIcon = (props) => (
    <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="12" y1="20" x2="12" y2="10" />
        <line x1="18" y1="20" x2="18" y2="4" />
        <line x1="6" y1="20" x2="6" y2="16" />
    </svg>
);

const SignatureIcon = (props) => (
    <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 10.5c-1 .5-2 1-3 1s-2-.5-3-1-2-1-3-1-2 .5-3 1-2 1-3 1" />
        <path d="M2 13.5c1 .5 2 1 3 1s2-.5 3-1 2-1 3-1 2 .5 3 1 2 1 3 1" />
        <path d="M18 10.5c-1.58 1.58-3.17 3.17-4.75 4.75" />
        <path d="m15 18-6-6" />
    </svg>
);

const XIcon = (props) => (
    <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
);

// Monochrome industry icons (inline SVGs, FA-like feel)
const IndustryIcon = ({ id, className = 'h-6 w-6' }) => {
  const common = { xmlns: 'http://www.w3.org/2000/svg', viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.8, strokeLinecap: 'round', strokeLinejoin: 'round', className };
  switch (id) {
    case 'painter': // minimal paint roller
      return (
        <svg {...common}>
          <rect x="5" y="5" width="10" height="4" rx="2"/>
          <path d="M15 7h3"/>
          <path d="M18 7v9"/>
        </svg>
      );
    case 'hvac': // fan
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="2"/>
          <path d="M12 4c2 0 3 1.5 3 3.5S13 11 12 11"/>
          <path d="M20 12c0 2-1.5 3-3.5 3S13 13 13 12"/>
          <path d="M12 20c-2 0-3-1.5-3-3.5S11 13 12 13"/>
          <path d="M4 12c0-2 1.5-3 3.5-3S11 11 11 12"/>
        </svg>
      );
    case 'pest-control': // minimal beetle
      return (
        <svg {...common}>
          {/* head */}
          <path d="M9.5 8a2.5 2.5 0 0 1 5 0"/>
          {/* body */}
          <rect x="8" y="10" width="8" height="7" rx="3"/>
          {/* center line */}
          <path d="M12 10v7"/>
          {/* legs left */}
          <path d="M8 12H5"/>
          <path d="M8 14.5H4.5"/>
          <path d="M8.5 16.5H6"/>
          {/* legs right */}
          <path d="M19 12h-3"/>
          <path d="M19.5 14.5H16"/>
          <path d="M18 16.5h-2.5"/>
          {/* antennae */}
          <path d="M10 6.5 8.5 5.5"/>
          <path d="M14 6.5 15.5 5.5"/>
        </svg>
      );
    case 'web-design': // monitor
      return (
        <svg {...common}>
          <rect x="3" y="5" width="18" height="12" rx="2"/>
          <path d="M8 21h8"/>
          <path d="M12 17v4"/>
        </svg>
      );
    case 'wedding-photography': // camera
      return (
        <svg {...common}>
          <rect x="4" y="7" width="16" height="12" rx="2"/>
          <circle cx="12" cy="13" r="3.5"/>
          <path d="M9 7l1.5-2h3L15 7"/>
        </svg>
      );
    case 'landscaping': // minimal leaf
      return (
        <svg {...common}>
          <path d="M12 4c-4 2-7 6-7 9 0 4 3 6 7 7 4-1 7-3 7-7 0-3-3-7-7-9Z"/>
          <path d="M12 7v11"/>
        </svg>
      );
    case 'roofing': // home/roof
      return (
        <svg {...common}>
          <path d="M3 11l9-7 9 7"/>
          <path d="M5 10v10h14V10"/>
        </svg>
      );
    case 'plumbing': // minimal faucet + drop
      return (
        <svg {...common}>
          {/* faucet body */}
          <path d="M6 8h6"/>
          {/* valve handle */}
          <path d="M9 6h2"/>
          <path d="M10 6v2"/>
          {/* spout */}
          <path d="M12 8v3"/>
          <path d="M12 11h3"/>
          {/* droplet */}
          <path d="M14 16c0 1.1-.9 2-2 2s-2-.9-2-2c0-.9.7-2 2-3 1.3 1 2 2.1 2 3Z"/>
        </svg>
      );
    case 'electrical': // bolt
      return (
        <svg {...common}>
          <path d="M13 2L3 14h7l-1 8 10-12h-7l1-8Z"/>
        </svg>
      );
    case 'solar': // sun
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="4"/>
          <path d="M12 2v3M12 19v3M4.2 4.2l2.1 2.1M17.7 17.7l2.1 2.1M2 12h3M19 12h3M4.2 19.8l2.1-2.1M17.7 6.3l2.1-2.1"/>
        </svg>
      );
    case 'pool-construction': // waves
      return (
        <svg {...common}>
          <path d="M3 12c1.5 1 3 1 4.5 0s3-1 4.5 0 3 1 4.5 0 3-1 4.5 0"/>
          <path d="M3 16c1.5 1 3 1 4.5 0s3-1 4.5 0 3 1 4.5 0 3-1 4.5 0"/>
        </svg>
      );
    case 'interior-design': // sofa
      return (
        <svg {...common}>
          <rect x="4" y="11" width="16" height="6" rx="2"/>
          <path d="M6 11V9a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v2"/>
          <path d="M4 17v2M20 17v2"/>
        </svg>
      );
    case 'catering': // minimal fork + spoon
      return (
        <svg {...common}>
          {/* fork */}
          <path d="M6 4v5"/>
          <path d="M8 4v5"/>
          <path d="M10 4v5"/>
          <path d="M8 9v12"/>
          {/* spoon */}
          <circle cx="17" cy="7" r="2"/>
          <path d="M17 9v12"/>
        </svg>
      );
    case 'auto-detailing': // minimal car silhouette
      return (
        <svg {...common}>
          {/* body silhouette */}
          <path d="M3 13h2l2-3h8l2 3h2"/>
          {/* wheels */}
          <circle cx="8" cy="16" r="1.6"/>
          <circle cx="16" cy="16" r="1.6"/>
          {/* base line */}
          <path d="M5 16h14"/>
          {/* window hint */}
          <path d="M9 10h6"/>
        </svg>
      );
    case 'videography': // video camera
      return (
        <svg {...common}>
          <rect x="3" y="7" width="11" height="10" rx="2"/>
          <path d="M14 9l7-3v12l-7-3V9Z"/>
        </svg>
      );
    case 'tree-service': // minimal pine
      return (
        <svg {...common}>
          <path d="M12 5l-4 4h8l-4-4Z"/>
          <path d="M12 9l-5 5h10l-5-5Z"/>
          <path d="M12 13l-6 6h12l-6-6Z"/>
          <path d="M12 13v6"/>
        </svg>
      );
    default:
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="8"/>
        </svg>
      );
  }
};


const LandingPage = () => {
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();

  // Structured data for homepage
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "QuoteTier",
    "description": "Interactive quote builder that helps customers upsell themselves. Good/Better/Best packages, financing, e-sign, and open tracking — all in one clean page. Free forever, $29/mo unlimited.",
    "url": "https://www.quotetier.com",
    "applicationCategory": "BusinessApplication",
    "operatingSystem": "Web",
    "offers": {
      "@type": "Offer",
      "name": "Free Forever",
      "description": "3 quotes per month free forever",
      "price": "0",
      "priceCurrency": "USD"
    },
    "creator": {
      "@type": "Organization",
      "name": "QuoteTier",
      "url": "https://www.quotetier.com"
    },
    "featureList": [
      "Good/Better/Best interactive packages",
      "Close rate +10pp with 4+ options",
      "42% financing rate with $/mo framing",
      "Average ticket lift $4,500 → $6,500+",
      "Financing and rebate fields built in",
      "Mobile-first e-sign and open tracking"
    ]
  };

  // Handle OAuth callback on homepage - redirect to proper callback handler
  useEffect(() => {
    const handleOAuthCallback = async () => {
      const urlParams = new URLSearchParams(location.search);
      const code = urlParams.get('code');
      const error_param = urlParams.get('error');

      if (code || error_param) {
        // OAuth callback landed on homepage instead of /auth/callback
        // Redirect to proper callback handler with all params intact
        navigate(`/auth/callback${location.search}`, { replace: true });
      }
    };

    handleOAuthCallback();
  }, [location.search, navigate]);

  useEffect(() => {
    // Check if AuthCallback was ever hit
    const authCallbackHit = localStorage.getItem('authCallbackHit');
    
    // Preserving your analytics tracking
    trackLandingPageView();
    
    // Track GA4 page view
    trackPageView('Landing Page - QuoteTier');
  }, []);

  useEffect(() => {
    const controlHeader = () => {
      const currentScrollY = window.scrollY;
      
      if (currentScrollY < lastScrollY || currentScrollY < 100) {
        // Scrolling up or near top - show header
        setIsVisible(true);
      } else {
        // Scrolling down - hide header
        setIsVisible(false);
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', controlHeader);
    return () => window.removeEventListener('scroll', controlHeader);
  }, [lastScrollY]);

  return (
    <>
      <SEO 
        title="Interactive Quotes That Upsell Themselves — Free Forever | QuoteTier"
        description="Turn flat-rate quotes into interactive Good/Better/Best packages customers actually enjoy. Close bigger deals without the sales pressure. Free forever for 3 quotes/mo. Pro: $29/mo unlimited."
        canonical="/"
        structuredData={structuredData}
        ogImage="/interactive-quote-builder-dashboard-software.png"
      />
      <header 
        className={`fixed top-4 left-1/2 z-50 w-[90%] max-w-4xl -translate-x-1/2 rounded-2xl border border-black/10 bg-white/95 shadow-lg transition-all duration-300 ease-in-out ${
          isVisible ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0'
        }`}
      >
        <div className="flex h-14 items-center justify-between px-4">
          <div className="flex items-center">
            <ZapIcon className="h-4 w-4 mr-2 text-primary" />
            <span className="font-instrument text-xl md:text-2xl">QuoteTier</span>
          </div>
          
          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-6 text-sm font-medium">
            <Link to="/pricing" className="font-instrument text-black text-xl hover:text-gray-700 transition-colors">
              Pricing
            </Link>
            <Link to="/blog/" className="font-instrument text-black text-xl hover:text-gray-700 transition-colors">
              Blog
            </Link>
            <Link to="/login" className="font-instrument text-black text-xl hover:text-gray-700 transition-colors">
              Sign In
            </Link>
            <Link to="/signup" onClick={() => analytics.track('CTA Clicked', { location: 'navigation', action: 'signup' })}>
              <Button className="font-instrument text-xl px-4 py-2 bg-black text-white hover:bg-gray-800 rounded-lg">
                Start Free
              </Button>
            </Link>
          </nav>
          
          {/* Mobile Navigation */}
          <div className="md:hidden flex items-center space-x-2">
            <Link to="/pricing" className="font-instrument text-sm px-3 py-2 text-black hover:text-gray-700 transition-colors">
              Pricing
            </Link>
            <Link to="/login" className="font-instrument text-sm px-3 py-2 text-black hover:text-gray-700 transition-colors">
              Sign In
            </Link>
            <Link to="/signup" onClick={() => analytics.track('CTA Clicked', { location: 'mobile-navigation', action: 'signup' })}>
              <Button className="font-instrument text-sm px-3 py-2 bg-black text-white hover:bg-gray-800 rounded-lg">
                Free
              </Button>
            </Link>
          </div>
        </div>
      </header>
    
    <div className="min-h-screen bg-background text-foreground">


      
      <main className="flex-1">
        {/* --- Hero Section --- */}
        <section className="py-32 md:py-40 lg:py-48">
          <div className="container max-w-7xl mx-auto px-4 text-center">
            <h1 className="font-instrument text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl tracking-tight leading-tight">
              Let your customers<br /><span className="text-primary font-bold text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl">upsell themselves.</span>
            </h1>
            <p className="font-jetbrains mt-6 md:mt-8 max-w-4xl mx-auto text-lg sm:text-xl md:text-2xl text-gray-600 tracking-tight leading-relaxed">
              Turn flat-rate quotes into interactive Good/Better/Best packages. Customers pick their own upgrades — you close bigger deals with zero sales pressure. Free forever, $29/mo for unlimited.
            </p>
            <div className="font-instrument mt-12 flex flex-col sm:flex-row gap-6 justify-center items-center">
              <Link to="/signup" onClick={() => analytics.track('CTA Clicked', { location: 'hero', action: 'signup' })}>
                <Button className="px-8 py-4 text-xl font-semibold bg-black text-white hover:bg-gray-800 rounded-lg shadow-lg">
                  Start Free — 3 Quotes/Mo
                </Button>
              </Link>
              <a href="#features">
                <Button className="px-8 py-4 text-xl font-semibold border-2 border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg" variant="outline" onClick={() => analytics.track('CTA Clicked', { location: 'hero', action: 'see-features' })}>
                  See How It Works
                </Button>
              </a>
            </div>
            <p className="font-jetbrains mt-10 text-lg text-gray-500">
              Free forever • 3 quotes per month • No credit card required
            </p>
          </div>
        </section>

        {/* --- Features Section --- */}
        <section id="features" className="py-24 md:py-36 bg-gray-50">
          <div className="container max-w-7xl mx-auto px-4">
            <div className="text-center max-w-3xl mx-auto">
            <h2 className="font-instrument text-5xl sm:text-6xl leading-snug tracking-wide">
                The Quote that sells for you.
              </h2>
              <p className="font-jetbrains mt-4 text-lg text-muted-foreground tracking-tight">
                QuoteTier gives your customers a sales experience they actually enjoy, helping you close bigger deals, faster.
              </p>
            </div>
            <div className="mt-16 grid gap-8 md:grid-cols-3">
              <Card className="border border-black">
                <CardHeader>
                  <div className="border border-black bg-primary/10 text-primary h-12 w-12 rounded-lg flex items-center justify-center mb-4">
                    <BarChartIcon className="h-6 w-6" />
                  </div>
                  <CardTitle className="font-instrument text-2xl">Higher Close Rates</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground font-jetbrains tracking-tight">Offering 3+ options instead of one flat rate gives customers agency. When they feel in control, they commit harder and faster.</p>
                </CardContent>
              </Card>
              <Card className="border border-black">
                <CardHeader>
                  <div className="border border-black bg-primary/10 text-primary h-12 w-12 rounded-lg flex items-center justify-center mb-4">
                    <ZapIcon className="h-6 w-6" />
                  </div>
                  <CardTitle className="font-instrument text-2xl">Bigger Ticket Sizes</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground font-jetbrains tracking-tight">Good/Better/Best framing naturally guides customers to higher-value options. Most end up in the middle or top tier — without any sales pressure.</p>
                </CardContent>
              </Card>
              <Card className="border border-black">
                <CardHeader>
                  <div className="border border-black bg-primary/10 text-primary h-12 w-12 rounded-lg flex items-center justify-center mb-4">
                    <SignatureIcon className="h-6 w-6" />
                  </div>
                  <CardTitle className="font-instrument text-2xl">Instant Acceptance</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground font-jetbrains tracking-tight">Built-in monthly payment framing and e-sign capture the yes while your customer is hot. No more print-sign-scan chase.</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* --- Dashboard Preview Section --- */}
        <section className="py-24 md:py-36 bg-white">
          <div className="container max-w-7xl mx-auto px-4">
            <div className="text-center max-w-4xl mx-auto mb-16">
              <h2 className="font-instrument text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
                See QuoteTier in Action
              </h2>
              <p className="font-jetbrains text-lg md:text-xl text-gray-600 tracking-tight leading-relaxed">
                Your professional dashboard where beautiful quotes come to life. Track engagement, manage customers, and watch conversion rates climb.
              </p>
            </div>
            
            <div className="relative mx-auto max-w-6xl">
              {/* Premium mock browser frame around screenshot */}
              <div className="relative rounded-xl border border-gray-200 shadow-lg overflow-hidden bg-white">
                <div className="flex items-center h-10 bg-gray-50 border-b border-gray-200 px-3">
                  <div className="flex items-center gap-1 mr-3">
                    <span className="inline-block w-2.5 h-2.5 rounded-full bg-gray-300"></span>
                    <span className="inline-block w-2.5 h-2.5 rounded-full bg-gray-300"></span>
                    <span className="inline-block w-2.5 h-2.5 rounded-full bg-gray-300"></span>
                  </div>
                  <div className="h-6 flex-1 max-w-md rounded-md bg-white border border-gray-200 text-[12px] text-gray-500 px-2 flex items-center truncate">
                    https://quotetier.com/app/dashboard
                  </div>
                </div>
                <img 
                  src="/interactive-quote-builder-dashboard-software.png" 
                  alt="QuoteTier Dashboard — clean, professional interface"
                  className="w-full h-auto block"
                  loading="lazy"
                  decoding="async"
                />
              </div>
              
            </div>
            
            <div className="text-center mt-12">
              <Link to="/signup" onClick={() => analytics.track('CTA Clicked', { location: 'dashboard-preview', action: 'signup' })}>
                <Button className="px-8 py-4 font-instrument text-xl font-semibold bg-black text-white hover:bg-gray-800 rounded-lg shadow-lg">
                  Start Free — 3 Quotes/Mo
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* --- Popular Solutions Section --- */}
        <section className="py-16 md:py-24">
          <div className="container max-w-6xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="font-instrument text-3xl md:text-4xl font-bold tracking-tight mb-4">
                Popular Solutions
              </h2>
              <p className="font-jetbrains text-lg text-gray-600 max-w-2xl mx-auto">
                Specialized quoting software designed for your industry's unique needs
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Link 
                to="/hvac-quoting-software"
                className="group"
                onClick={() => analytics.track('Landing Page Link Clicked', { destination: 'hvac-quoting-software', location: 'popular-solutions' })}
              >
                <Card className="h-full border border-gray-200 hover:border-black hover:shadow-lg transition-all duration-300 group">
                  <CardHeader className="pb-3">
                    <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center mb-3">
                      <IndustryIcon id="hvac" className="h-6 w-6" />
                    </div>
                    <CardTitle className="font-instrument text-lg font-semibold">HVAC Quoting</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="font-jetbrains text-sm text-gray-600">
                      Interactive HVAC quotes with Good/Better/Best system packages that increase average job value
                    </p>
                  </CardContent>
                </Card>
              </Link>

              <Link 
                to="/painting-quote-software"
                className="group"
                onClick={() => analytics.track('Landing Page Link Clicked', { destination: 'painting-quote-software', location: 'popular-solutions' })}
              >
                <Card className="h-full border border-gray-200 hover:border-black hover:shadow-lg transition-all duration-300 group">
                  <CardHeader className="pb-3">
                    <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center mb-3">
                      <IndustryIcon id="painter" className="h-6 w-6" />
                    </div>
                    <CardTitle className="font-instrument text-lg font-semibold">Painting Quotes</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="font-jetbrains text-sm text-gray-600">
                      Let customers choose paint quality, coats, and warranty options with transparent pricing
                    </p>
                  </CardContent>
                </Card>
              </Link>

              <Link 
                to="/web-developer-quote-software"
                className="group"
                onClick={() => analytics.track('Landing Page Link Clicked', { destination: 'web-developer-quote-software', location: 'popular-solutions' })}
              >
                <Card className="h-full border border-gray-200 hover:border-black hover:shadow-lg transition-all duration-300 group">
                  <CardHeader className="pb-3">
                    <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center mb-3">
                      <IndustryIcon id="web-design" className="h-6 w-6" />
                    </div>
                    <CardTitle className="font-instrument text-lg font-semibold">Web Development</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="font-jetbrains text-sm text-gray-600">
                      Professional project scoping with features, timelines, and support level options
                    </p>
                  </CardContent>
                </Card>
              </Link>

              <Link
                to="/videographer-quote-software"
                className="group"
                onClick={() => analytics.track('Landing Page Link Clicked', { destination: 'videographer-quote-software', location: 'popular-solutions' })}
              >
                <Card className="h-full border border-gray-200 hover:border-black hover:shadow-lg transition-all duration-300 group">
                  <CardHeader className="pb-3">
                    <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center mb-3">
                      <IndustryIcon id="videographer" className="h-6 w-6" />
                    </div>
                    <CardTitle className="font-instrument text-lg font-semibold">Videography</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="font-jetbrains text-sm text-gray-600">
                      Video production quotes with length, editing style, and delivery package options
                    </p>
                  </CardContent>
                </Card>
              </Link>
            </div>

            {/* Complete industry index — lifts internal link coverage so every industry page has a path from the homepage, not just sitemap. */}
            <div className="mt-12 pt-10 border-t border-gray-200">
              <h3 className="font-jetbrains text-sm text-gray-500 uppercase tracking-widest mb-5 text-center">All industries we serve</h3>
              <div className="flex flex-wrap justify-center gap-2">
                <Link to="/hvac-quoting-software" className="px-4 py-2 font-jetbrains text-sm border border-gray-300 rounded-full hover:border-black hover:bg-black hover:text-white transition-colors">HVAC</Link>
                <Link to="/plumbing-quoting-software" className="px-4 py-2 font-jetbrains text-sm border border-gray-300 rounded-full hover:border-black hover:bg-black hover:text-white transition-colors">Plumbing</Link>
                <Link to="/electrical-quoting-software" className="px-4 py-2 font-jetbrains text-sm border border-gray-300 rounded-full hover:border-black hover:bg-black hover:text-white transition-colors">Electrical</Link>
                <Link to="/construction-quoting-software" className="px-4 py-2 font-jetbrains text-sm border border-gray-300 rounded-full hover:border-black hover:bg-black hover:text-white transition-colors">Construction</Link>
                <Link to="/roofing-quoting-software" className="px-4 py-2 font-jetbrains text-sm border border-gray-300 rounded-full hover:border-black hover:bg-black hover:text-white transition-colors">Roofing</Link>
                <Link to="/solar-quoting-software" className="px-4 py-2 font-jetbrains text-sm border border-gray-300 rounded-full hover:border-black hover:bg-black hover:text-white transition-colors">Solar</Link>
                <Link to="/painting-quote-software" className="px-4 py-2 font-jetbrains text-sm border border-gray-300 rounded-full hover:border-black hover:bg-black hover:text-white transition-colors">Painting</Link>
                <Link to="/flooring-quoting-software" className="px-4 py-2 font-jetbrains text-sm border border-gray-300 rounded-full hover:border-black hover:bg-black hover:text-white transition-colors">Flooring</Link>
                <Link to="/cabinet-quoting-software" className="px-4 py-2 font-jetbrains text-sm border border-gray-300 rounded-full hover:border-black hover:bg-black hover:text-white transition-colors">Cabinet</Link>
                <Link to="/window-door-quoting-software" className="px-4 py-2 font-jetbrains text-sm border border-gray-300 rounded-full hover:border-black hover:bg-black hover:text-white transition-colors">Window &amp; Door</Link>
                <Link to="/handyman-quoting-software" className="px-4 py-2 font-jetbrains text-sm border border-gray-300 rounded-full hover:border-black hover:bg-black hover:text-white transition-colors">Handyman</Link>
                <Link to="/landscape-quoting-software" className="px-4 py-2 font-jetbrains text-sm border border-gray-300 rounded-full hover:border-black hover:bg-black hover:text-white transition-colors">Landscaping</Link>
                <Link to="/tree-service-quoting-software" className="px-4 py-2 font-jetbrains text-sm border border-gray-300 rounded-full hover:border-black hover:bg-black hover:text-white transition-colors">Tree Service</Link>
                <Link to="/pool-construction-quoting-software" className="px-4 py-2 font-jetbrains text-sm border border-gray-300 rounded-full hover:border-black hover:bg-black hover:text-white transition-colors">Pool Construction</Link>
                <Link to="/pest-control-quoting-software" className="px-4 py-2 font-jetbrains text-sm border border-gray-300 rounded-full hover:border-black hover:bg-black hover:text-white transition-colors">Pest Control</Link>
                <Link to="/catering-quoting-software" className="px-4 py-2 font-jetbrains text-sm border border-gray-300 rounded-full hover:border-black hover:bg-black hover:text-white transition-colors">Catering</Link>
                <Link to="/interior-design-quoting-software" className="px-4 py-2 font-jetbrains text-sm border border-gray-300 rounded-full hover:border-black hover:bg-black hover:text-white transition-colors">Interior Design</Link>
                <Link to="/auto-detailing-quoting-software" className="px-4 py-2 font-jetbrains text-sm border border-gray-300 rounded-full hover:border-black hover:bg-black hover:text-white transition-colors">Auto Detailing</Link>
                <Link to="/auto-body-estimating-software" className="px-4 py-2 font-jetbrains text-sm border border-gray-300 rounded-full hover:border-black hover:bg-black hover:text-white transition-colors">Auto Body</Link>
                <Link to="/videographer-quote-software" className="px-4 py-2 font-jetbrains text-sm border border-gray-300 rounded-full hover:border-black hover:bg-black hover:text-white transition-colors">Videography</Link>
                <Link to="/wedding-photography-quote-software" className="px-4 py-2 font-jetbrains text-sm border border-gray-300 rounded-full hover:border-black hover:bg-black hover:text-white transition-colors">Wedding Photography</Link>
                <Link to="/web-developer-quote-software" className="px-4 py-2 font-jetbrains text-sm border border-gray-300 rounded-full hover:border-black hover:bg-black hover:text-white transition-colors">Web Developer</Link>
                <Link to="/msp-quoting-software" className="px-4 py-2 font-jetbrains text-sm border border-gray-300 rounded-full hover:border-black hover:bg-black hover:text-white transition-colors">MSP / IT</Link>
                <Link to="/manufacturing-quoting-software" className="px-4 py-2 font-jetbrains text-sm border border-gray-300 rounded-full hover:border-black hover:bg-black hover:text-white transition-colors">Manufacturing</Link>
                <Link to="/sign-shop-quoting-software" className="px-4 py-2 font-jetbrains text-sm border border-gray-300 rounded-full hover:border-black hover:bg-black hover:text-white transition-colors">Signage</Link>
                <Link to="/small-business-quoting-software" className="px-4 py-2 font-jetbrains text-sm border border-gray-300 rounded-full hover:border-black hover:bg-black hover:text-white transition-colors">Small Business</Link>
              </div>
            </div>
          </div>
        </section>

        {/* --- Demo Quotes Showcase Section --- */}
        <section className="py-24 md:py-36 bg-gradient-to-br from-gray-50 to-gray-100">
          <div className="container max-w-7xl mx-auto px-4">
            <div className="text-center max-w-4xl mx-auto mb-16">
              <h2 className="font-instrument text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
                Perfect for Every Industry
              </h2>
              <p className="font-jetbrains text-lg md:text-xl text-gray-600 tracking-tight leading-relaxed">
                See how QuoteTier transforms quotes across different industries. Click any example to experience the interactive magic your customers will love.
              </p>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
              {industryQuotes.map((industry) => (
                <Link 
                  key={industry.id}
                  to={`/demo-quote/${industry.id}`}
                  onClick={() => analytics.track('Demo Quote Clicked', { industry: industry.name })}
                  className="group"
                >
                  <Card className="relative h-full rounded-xl border border-gray-200 bg-white transition-all duration-300 hover:shadow-lg hover:border-black/80 group focus-within:ring-2 focus-within:ring-black/10">
                    <CardHeader className="pb-2">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-lg border border-gray-200 bg-white flex items-center justify-center">
                          <IndustryIcon id={industry.id} className="h-6 w-6 text-gray-900" />
                        </div>
                        <div className="min-w-0">
                          <CardTitle className="font-instrument text-lg font-semibold text-gray-900 tracking-tight truncate">{industry.name}</CardTitle>
                          {/* removed per request: curated example pill */}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-2">
                      <CardDescription className="font-jetbrains text-sm text-gray-600 leading-relaxed">
                        {industry.description}
                      </CardDescription>
                      {/* Price removed per request for a cleaner, non-price-focused showcase */}
                    </CardContent>
                    <CardFooter className="pt-4">
                      <Button 
                        variant="outline"
                        className="w-full border-gray-300 hover:border-black hover:bg-black hover:text-white transition-all font-instrument tracking-tight"
                      >
                        View Interactive Demo
                        <span className="ml-2 inline-block transition-transform group-hover:translate-x-0.5">→</span>
                      </Button>
                    </CardFooter>
                  </Card>
                </Link>
              ))}
            </div>
            
            {/* Call to Action */}
            <div className="text-center mt-16">
              <p className="font-jetbrains text-gray-600 mb-8 text-lg">
                Don't see your industry? QuoteTier works for any business that sends quotes.
              </p>
              
              <Link to="/signup" onClick={() => analytics.track('CTA Clicked', { location: 'demo-quotes', action: 'signup' })}>
                <Button className="px-10 py-4 font-instrument text-xl font-semibold bg-black text-white hover:bg-gray-800 rounded-lg shadow-lg">
                  Create Your First Quote Free
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* --- Comparison Section --- */}
        <section className="py-16 md:py-24 bg-white">
          <div className="container max-w-6xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="font-instrument text-3xl md:text-4xl font-bold tracking-tight mb-4">
                Why QuoteTier beats PDFs
              </h2>
              <p className="font-jetbrains text-lg text-gray-600 max-w-2xl mx-auto">
                The numbers don't lie. Interactive proposals convert higher and close bigger.
              </p>
            </div>
            
            {/* Desktop Table */}
            <div className="hidden md:block bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-6 px-6 font-instrument text-lg font-semibold text-gray-900 bg-gray-50">
                      Features
                    </th>
                    <th className="text-center py-6 px-6 bg-black text-white">
                      <div className="flex flex-col items-center gap-2">
                        <div className="flex items-center gap-2">
                          <ZapIcon className="h-5 w-5" />
                          <span className="font-instrument text-lg font-bold">QuoteTier</span>
                        </div>
                        <span className="bg-white/20 text-white text-xs font-medium px-3 py-1 rounded-full">
                          Recommended
                        </span>
                      </div>
                    </th>
                    <th className="text-center py-6 px-6 font-instrument text-lg font-semibold text-gray-700 bg-gray-50">
                      PDF Quotes
                    </th>
                    <th className="text-center py-6 px-6 font-instrument text-lg font-semibold text-gray-700 bg-gray-50">
                      Other Software
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-gray-100 hover:bg-gray-50/50 transition-colors">
                    <td className="py-4 px-6 font-jetbrains font-medium">Time-stamped quote opens</td>
                    <td className="py-4 px-6 text-center"><CheckIcon className="h-5 w-5 text-green-600 mx-auto" /></td>
                    <td className="py-4 px-6 text-center"><XIcon className="h-5 w-5 text-gray-300 mx-auto" /></td>
                    <td className="py-4 px-6 text-center"><XIcon className="h-5 w-5 text-gray-300 mx-auto" /></td>
                  </tr>
                  <tr className="border-b border-gray-100 bg-gray-50/30 hover:bg-gray-50/50 transition-colors">
                    <td className="py-4 px-6 font-jetbrains font-medium">Interactive upselling</td>
                    <td className="py-4 px-6 text-center"><CheckIcon className="h-5 w-5 text-green-600 mx-auto" /></td>
                    <td className="py-4 px-6 text-center"><XIcon className="h-5 w-5 text-gray-300 mx-auto" /></td>
                    <td className="py-4 px-6 text-center"><XIcon className="h-5 w-5 text-gray-300 mx-auto" /></td>
                  </tr>
                  <tr className="border-b border-gray-100 hover:bg-gray-50/50 transition-colors">
                    <td className="py-4 px-6 font-jetbrains font-medium">Legally binding e-signatures</td>
                    <td className="py-4 px-6 text-center"><CheckIcon className="h-5 w-5 text-green-600 mx-auto" /></td>
                    <td className="py-4 px-6 text-center"><XIcon className="h-5 w-5 text-gray-300 mx-auto" /></td>
                    <td className="py-4 px-6 text-center">
                      <div className="w-5 h-5 rounded-full bg-yellow-100 flex items-center justify-center mx-auto">
                        <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
                      </div>
                    </td>
                  </tr>
                  <tr className="border-b border-gray-100 bg-gray-50/30 hover:bg-gray-50/50 transition-colors">
                    <td className="py-4 px-6 font-jetbrains font-medium">Mobile-optimized experience</td>
                    <td className="py-4 px-6 text-center"><CheckIcon className="h-5 w-5 text-green-600 mx-auto" /></td>
                    <td className="py-4 px-6 text-center"><XIcon className="h-5 w-5 text-gray-300 mx-auto" /></td>
                    <td className="py-4 px-6 text-center">
                      <div className="w-5 h-5 rounded-full bg-yellow-100 flex items-center justify-center mx-auto">
                        <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
                      </div>
                    </td>
                  </tr>
                  <tr className="border-b border-gray-100 hover:bg-gray-50/50 transition-colors">
                    <td className="py-4 px-6 font-jetbrains font-medium">Real-time engagement tracking</td>
                    <td className="py-4 px-6 text-center"><CheckIcon className="h-5 w-5 text-green-600 mx-auto" /></td>
                    <td className="py-4 px-6 text-center"><XIcon className="h-5 w-5 text-gray-300 mx-auto" /></td>
                    <td className="py-4 px-6 text-center"><XIcon className="h-5 w-5 text-gray-300 mx-auto" /></td>
                  </tr>
                  <tr className="bg-gray-50/30">
                    <td className="py-4 px-6 font-jetbrains font-medium">Setup time</td>
                    <td className="py-4 px-6 text-center">
                      <span className="font-jetbrains font-semibold text-green-600 bg-green-50 px-3 py-1 rounded-full text-sm">
                        5 minutes
                      </span>
                    </td>
                    <td className="py-4 px-6 text-center">
                      <span className="font-jetbrains text-gray-600 text-sm">Manual process</span>
                    </td>
                    <td className="py-4 px-6 text-center">
                      <span className="font-jetbrains text-gray-600 text-sm">Hours/Days</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden space-y-6">
              <div className="bg-black rounded-2xl p-6 text-white">
                <div className="flex items-center justify-center gap-2 mb-6">
                  <ZapIcon className="h-6 w-6" />
                  <h3 className="font-instrument text-xl font-bold">QuoteTier</h3>
                  <span className="bg-white/20 text-white text-xs font-medium px-3 py-1 rounded-full ml-2">
                    Recommended
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <CheckIcon className="h-4 w-4 text-green-400" />
                      <span className="font-jetbrains">Time-stamped opens</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckIcon className="h-4 w-4 text-green-400" />
                      <span className="font-jetbrains">Interactive upselling</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckIcon className="h-4 w-4 text-green-400" />
                      <span className="font-jetbrains">Legal e-signatures</span>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <CheckIcon className="h-4 w-4 text-green-400" />
                      <span className="font-jetbrains">Mobile optimized</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckIcon className="h-4 w-4 text-green-400" />
                      <span className="font-jetbrains">Real-time tracking</span>
                    </div>
                    <div className="bg-green-400/20 px-3 py-1 rounded-full">
                      <span className="font-jetbrains text-sm font-medium text-green-100">5 min setup</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                  <h3 className="font-instrument text-lg font-semibold text-gray-800 mb-4 text-center">
                    PDF Quotes
                  </h3>
                  <div className="space-y-3 text-sm">
                    <div className="flex items-center gap-2">
                      <XIcon className="h-4 w-4 text-gray-300" />
                      <span className="font-jetbrains text-gray-600">No tracking</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <XIcon className="h-4 w-4 text-gray-300" />
                      <span className="font-jetbrains text-gray-600">No upselling</span>
                    </div>
                    <div className="bg-red-50 px-3 py-1 rounded-full">
                      <span className="font-jetbrains text-sm font-medium text-red-600">Manual process</span>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                  <h3 className="font-instrument text-lg font-semibold text-gray-800 mb-4 text-center">
                    Other Software
                  </h3>
                  <div className="space-y-3 text-sm">
                    <div className="flex items-center gap-2">
                      <XIcon className="h-4 w-4 text-gray-300" />
                      <span className="font-jetbrains text-gray-600">No upselling</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded-full bg-yellow-100 flex items-center justify-center">
                        <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
                      </div>
                      <span className="font-jetbrains text-gray-600">Partial features</span>
                    </div>
                    <div className="bg-red-50 px-3 py-1 rounded-full">
                      <span className="font-jetbrains text-sm font-medium text-red-600">Complex setup</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="text-center mt-8">
              <Link to="/signup" onClick={() => analytics.track('CTA Clicked', { location: 'comparison', action: 'signup' })}>
                <Button className="px-8 py-3 font-instrument text-lg font-semibold bg-black text-white hover:bg-gray-800 rounded-lg">
                  Start Free
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* --- Final CTA --- */}
        <section className="py-24 md:py-36">
          <div className="container max-w-3xl mx-auto px-4 text-center">
            <h2 className="font-instrument text-5xl md:text-6xl font-bold tracking-tight">Ready to get more from every quote?</h2>
            <p className="font-jetbrains tracking-tight mt-6 text-xl text-gray-600 max-w-2xl mx-auto">
              Free forever for 3 quotes/mo. Upgrade to Pro for $29/mo when you're ready for unlimited.
            </p>
            <div className="mt-10">
              <Link to="/signup" onClick={() => analytics.track('CTA Clicked', { location: 'final-cta', action: 'signup' })}>
                <Button className="px-10 py-4 font-instrument text-xl font-semibold bg-black text-white hover:bg-gray-800 rounded-lg shadow-lg">
                  Start Free with QuoteTier
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </main>

      {/* --- Footer --- */}
      <Footer />
    </div>
    </>
  );
};

export default LandingPage;
